import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../../src/components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import {
  TContext,
  TPrivilege,
  TResponseModel,
  TRole,
  TUser,
} from "../../../types/config";
import { TFilterModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  dateOnlyFormat,
  defaultFilterParams,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import {
  Button,
  Drawer,
  Space,
  Input,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import RoleForm from "./form";
import CustomDrawer from "../../../components/drawer";
import { GetRoleList } from "../../../utils/api.constant";
import { UserContext } from "../../../context";
import { useNavigate } from "react-router-dom";
import utils from "../../../utils";
import ActionCard from "../../../components/actionCard/actionCard";
import {
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  TeamOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import DateUtils from "../../../utils/date";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import { useColumnSearch } from "../../../components/table/column-search";

const ROLE_LIST_COLUMN_KEY = "roleList";

export default function RoleList() {
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TUser[]>([]);
  const { onShowToast } = useToast();
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { getColumnSearchProps } = useColumnSearch<TRole>();
  const context: TContext = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user as TUser);
  const [stakeHolderName, setStakeHolderName] = useState<string>(
    activeUser?.stakeHolderName || STAKE_HOLDERS.IDEX
  );

  useEffect(() => {
    (async () => {
      const savedPreferences =
        await loadColumnPreferences(ROLE_LIST_COLUMN_KEY);
      console.log({ savedPreferences });
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col])
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
  }, []);

  useEffect(() => {
    setActiveUser(context?.user as TUser);
  }, [context?.user]);

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    saveColumnPreferences(ROLE_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false); // Close the drawer
  };

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "name", // Ensure this key exists in TUser
      key: "name",
      // width: "20%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TRole, b: TRole) => a.name?.localeCompare(b.name ?? "") || 0,
      onFilter: (value: string | number | boolean, record: TRole) =>
        record.name?.toLowerCase() === (value as string).toLowerCase(),
    },
    // {
    //   title: "Unique ID",
    //   dataIndex: "roleUniqueId",
    //   key: "roleUniqueId",
    //   width: "30%",
    //   filterSearch: true,
    //   sorter: (a: TRole, b: TRole) =>
    //     a.roleUniqueId?.localeCompare(b.roleUniqueId ?? "") || 0,
    //   filters: utils.getUniqueFilters(data, "roleUniqueId"),
    //   onFilter: (value: string | number | boolean, record: TRole) =>
    //     record.roleUniqueId?.includes(value as string),
    // },
    {
      title: "Stake Holder",
      dataIndex: "stakeHolderName",
      key: "stakeHolderName",
      // width: 200,
      filterSearch: true,
      sorter: (a: TRole, b: TRole) =>
        a.stakeHolderName?.localeCompare(b.stakeHolderName ?? "") || 0,
      filters: utils.getUniqueFilters(data, "stakeHolderName"),
      onFilter: (value: string | number | boolean, record: TRole) =>
        record.stakeHolderName?.toLowerCase() ===
        (value as string).toLowerCase(),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      // width: 200,
      filterSearch: true,
      sorter: (a: TRole, b: TRole) =>
        a.description?.localeCompare(b.description ?? "") || 0,
      filters: utils.getUniqueFilters(data, "description"),
      ...getColumnSearchProps("description"),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      // width: 120,
      render: (user: TUser) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {Utils.isUserHasAccess(
                  context?.user?.privilegeList as TPrivilege[],
                  ePrivileges.EDITROLES
                ) && (
                  <CustomDrawer
                    size="large"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={RoleForm}
                    componentProps={{
                      id: user.id,
                      onRefreshList: () => fetchData(),
                    }}
                    title="Role Form"
                  />
                )}
              </div>
            }
            icon={
              <MoreOutlined
                style={{
                  fontSize: "20px",
                  cursor: "pointer",
                  display: Utils.isUserHasAccess(
                    context?.user?.privilegeList as TPrivilege[],
                    ePrivileges.EDITROLES
                  )
                    ? "visible"
                    : "none",
                }}
              />
            }
          />
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setColumns(initialColumns);
  }, [context?.user?.privilegeList]);

  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns); // Staging state for changes
  // To fetch the list of all the users
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
          stakeHolderName,
        },
      };
      const response: TResponseModel = await post(GetRoleList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TRole, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          name: item.name,
          roleUniqueId: item.roleUniqueId,
          stakeHolderName: item.stakeHolderName,
          description: item.description,
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(data.map((item: TRole) => item.name))
        ).map((name) => ({ text: name, value: name }));
        const roleUniqueIdFilters = Array.from(
          new Set(data.map((item: TRole) => item.roleUniqueId))
        ).map((roleUniqueId) => ({ text: roleUniqueId, value: roleUniqueId }));
        const stakeHolderNameFilters = Array.from(
          new Set(data.map((item: TRole) => item.stakeHolderName))
        ).map((stakeHolderName) => ({
          text: stakeHolderName,
          value: stakeHolderName,
        }));
        const descriptionFilters = Array.from(
          new Set(data.map((item: TRole) => item.description))
        ).map((description) => ({ text: description, value: description }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: nameFilters };
            }
            if (col.key === "roleUniqueId") {
              return { ...col, filters: roleUniqueIdFilters };
            }
            if (col.key === "stakeHolderName") {
              return { ...col, filters: stakeHolderNameFilters };
            }
            if (col.key === "description") {
              return { ...col, filters: descriptionFilters };
            }
            return col;
          })
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const getFilteredData = () => {
    let result = [...data];

    columns.forEach((col) => {
      const dataKey = col.dataIndex || col.key;
      const filteredValues: any[] = col.filteredValue || null;
      if (
        filteredValues &&
        Array.isArray(filteredValues) &&
        filteredValues.length
      ) {
        result = result.filter((item) => {
          const val = (item as any)[dataKey];
          if (val === null || val === undefined) return false;
          return filteredValues.some(
            (fv) =>
              val?.toString().toLowerCase() === fv?.toString().toLowerCase()
          );
        });
      }
    });

    if (searchText && searchText.trim() !== "") {
      const text = searchText.toLowerCase();
      result = result.filter((item) =>
        Object.values(item).some((val) =>
          val?.toString().toLowerCase().includes(text)
        )
      );
    }

    return result;
  };

  const filteredData = getFilteredData();

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
    clearColumnPreferences(ROLE_LIST_COLUMN_KEY);
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<TeamOutlined />}
        title={`${filterParams.totalRows} Total Roles`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList as TPrivilege[],
            ePrivileges.ADDROLES
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={RoleForm}
              componentProps={{
                id: 0,
                activeStake: stakeHolderName,
                onRefreshList: () => {
                  navigate("/dashboard/roles");
                  fetchData();
                },
              }}
              title="Role Form"
              size="large"
            />
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={data}
            filteredData={filteredData}
            allColumns={initialColumns}
            visibleColumns={visibleColumns}
            moduleName="Role List"
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],

            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          scroll={{ x: "max-content" }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
